import { useAppType, useCountdown, useLogger, useSettings } from '@capsicum/common';
import { useCallback } from 'react';

import {
	postEmailGenerateCode,
	postEmailGenerateCodeByMobileNumber,
	postGenerateWhatsappCodeByEmail,
	postGenerateWhatsappCodeByMobileNumber,
	postMobileGenerateCode,
	postSendEmailCodeForRegistration,
	postSendSMSCodeForRegistration
} from '../data-access';
import {
	type EmailGenerateCodeParams,
	type EmailGenerateCodeParamsOptCatchup,
	type MobileNumberGenerateCodeParams,
	type MobileNumberValidationParams
} from '../models';

type GetFingerprintDataResponse = {
	requestId: string;
	visitorId: string;
};

export const useSendVerificationCode = ({ appHash }: { appHash?: string } = {}) => {
	const { seconds: timeUntilEligibleForRetry, start } = useCountdown({ minutes: 1, seconds: 0 });
	const appType = useAppType();
	const logger = useLogger();
	const { settings } = useSettings();
	const isFingerprintEnabled = Boolean(settings?.fingerprintJSConfig?.enabled);

	const getFingerprintDataSafe = useCallback(
		async (
			getFingerprintData?: () => Promise<GetFingerprintDataResponse | null>
		): Promise<GetFingerprintDataResponse> => {
			if (isFingerprintEnabled && typeof getFingerprintData === 'function') {
				try {
					return await getFingerprintData().then(res => ({
						requestId: res?.requestId ?? '',
						visitorId: res?.visitorId ?? ''
					}));
				} catch (err) {
					logger.error('Could not generate fingerprint data', { err });
				}
			}

			return { requestId: '', visitorId: '' };
		},
		[isFingerprintEnabled, logger]
	);

	const sendSmsCodeToUser = useCallback(
		async (
			params: MobileNumberGenerateCodeParams,
			getFingerprintData?: () => Promise<GetFingerprintDataResponse | null>
		) => {
			const fingerprintData = await getFingerprintDataSafe(getFingerprintData);

			await postMobileGenerateCode({
				...params,
				appHash,
				captchaInformation: { ...params.captchaInformation, appContext: 'capsicum', appType, version: 'v4' },
				...fingerprintData
			});

			// Starting the countdown timer till the user is eligible for the next try to send the code
			start();
		},
		[appHash, appType, getFingerprintDataSafe, start]
	);

	const sendEmailCodeByMobileNumberToUser = useCallback(
		async (params: MobileNumberValidationParams) => {
			await postEmailGenerateCodeByMobileNumber(params);

			// Starting the countdown timer till the user is eligible for the next try to send the code
			start();
		},
		[start]
	);

	const sendEmailCodeToUser = useCallback(
		async (params: EmailGenerateCodeParamsOptCatchup) => {
			await postEmailGenerateCode({
				...params,
				captchaInformation: params.captchaInformation
					? { ...params.captchaInformation, appContext: 'capsicum', appType, version: 'v4' }
					: undefined
			});

			// Starting the countdown timer till the user is eligible for the next try to send the code
			start();
		},
		[appType, start]
	);

	const sendSmsCodeToUserOnRegistration = useCallback(
		async (
			params: MobileNumberGenerateCodeParams,
			getFingerprintData?: () => Promise<GetFingerprintDataResponse | null>
		) => {
			const fingerprintData = await getFingerprintDataSafe(getFingerprintData);

			await postSendSMSCodeForRegistration({
				...params,
				appHash,
				captchaInformation: { ...params.captchaInformation, appContext: 'capsicum', appType, version: 'v4' },
				...fingerprintData
			});

			// Starting the countdown timer till the user is eligible for the next try to send the code
			start();
		},
		[appHash, appType, getFingerprintDataSafe, start]
	);

	const sendEmailCodeToUserOnRegistration = useCallback(
		async (params: EmailGenerateCodeParams) => {
			await postSendEmailCodeForRegistration({
				...params,
				captchaInformation: { ...params.captchaInformation, appContext: 'capsicum', appType, version: 'v4' }
			});

			// Starting the countdown timer till the user is eligible for the next try to send the code
			start();
		},
		[appType, start]
	);

	const sendWhatsappCodeByPhoneNumberToUser = useCallback(
		async (params: MobileNumberGenerateCodeParams) => {
			await postGenerateWhatsappCodeByMobileNumber({
				...params,
				captchaInformation: { ...params.captchaInformation, appContext: 'capsicum', appType, version: 'v4' }
			});

			// Starting the countdown timer till the user is eligible for the next try to send the code
			start();
		},
		[appType, start]
	);

	const sendWhatsappCodeByEmailToUser = useCallback(
		async (params: EmailGenerateCodeParams) => {
			await postGenerateWhatsappCodeByEmail({
				...params,
				captchaInformation: { ...params.captchaInformation, appContext: 'capsicum', appType, version: 'v4' }
			});

			// Starting the countdown timer till the user is eligible for the next try to send the code
			start();
		},
		[appType, start]
	);

	return {
		sendEmailCodeByMobileNumberToUser,
		sendEmailCodeToUser,
		sendEmailCodeToUserOnRegistration,
		sendSmsCodeToUser,
		sendSmsCodeToUserOnRegistration,
		sendWhatsappCodeByEmailToUser,
		sendWhatsappCodeByPhoneNumberToUser,
		timeUntilEligibleForRetry: timeUntilEligibleForRetry || 0
	};
};
